import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'
import type { User, LoginCredentials } from '@/types'

/** Decode JWT payload without verifying signature (client-side only). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64))
  } catch {
    return null
  }
}

const SESSION_WARNING_MINUTES = 15

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const initialized = ref(false)
  const sessionExpiringSoon = ref(false)
  const sessionExpiresAt = ref<Date | null>(null)

  let expirationTimer: ReturnType<typeof setTimeout> | null = null

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isEmployee = computed(() => user.value?.role === 'EMPLOYEE')
  const isProjectLead = computed(() => user.value?.role === 'PROJECT_LEAD')
  const isManager = computed(() => user.value?.role === 'MANAGER')
  // Manager = Admin in this system (no separate admin role)
  const isAdmin = computed(() => user.value?.role === 'MANAGER')

  function startExpirationTimer(jwt: string) {
    clearExpirationTimer()
    const payload = decodeJwtPayload(jwt)
    if (!payload || typeof payload.exp !== 'number') return

    const expiresMs = payload.exp * 1000
    sessionExpiresAt.value = new Date(expiresMs)
    const warnAt = expiresMs - SESSION_WARNING_MINUTES * 60 * 1000
    const delay = warnAt - Date.now()

    if (delay <= 0) {
      // Already within warning window
      sessionExpiringSoon.value = true
      return
    }

    expirationTimer = setTimeout(() => {
      sessionExpiringSoon.value = true
    }, delay)
  }

  function clearExpirationTimer() {
    if (expirationTimer) {
      clearTimeout(expirationTimer)
      expirationTimer = null
    }
    sessionExpiringSoon.value = false
    sessionExpiresAt.value = null
  }

  async function initialize() {
    const storedToken = localStorage.getItem('hermes_token')
    if (storedToken) {
      token.value = storedToken
      api.setToken(storedToken)
      try {
        await fetchCurrentUser()
        startExpirationTimer(storedToken)
      } catch {
        // Token invalid, clear it
        logout()
      }
    }
    initialized.value = true
  }

  async function login(credentials: LoginCredentials) {
    const response = await api.login(credentials)
    token.value = response.access_token
    localStorage.setItem('hermes_token', response.access_token)
    api.setToken(response.access_token)
    await fetchCurrentUser()
    startExpirationTimer(response.access_token)
  }

  async function fetchCurrentUser() {
    user.value = await api.getCurrentUser()
  }

  function logout() {
    clearExpirationTimer()
    user.value = null
    token.value = null
    localStorage.removeItem('hermes_token')
    api.clearToken()
  }

  return {
    user,
    token,
    initialized,
    isAuthenticated,
    isEmployee,
    isProjectLead,
    isManager,
    isAdmin,
    sessionExpiringSoon,
    sessionExpiresAt,
    initialize,
    login,
    logout,
    fetchCurrentUser,
  }
})
